import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Pencil, X, Trash2, Maximize2, Minimize2 } from 'lucide-react';
import GridEditor from './editors/GridEditor';
import StandEditor from './editors/StandEditor';
import { useCanvasDrawing } from '../hooks/useCanvasDrawing';
import { useLayoutManager } from '../hooks/useLayoutManager';
import { CanvasToolbar, COLUMN_TYPES } from './canvas/CanvasToolbar';
import { StandHoverMenu } from './canvas/StandHoverMenu';
import { StandSidesDialog } from './editors/StandSidesDialog';
import { CeilingZoneModal } from './canvas/CeilingZoneModal';

const CELL_SIZE = 20;

export default function LayoutEditor({ exhibition, hall }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const menuHoveredRef    = useRef(false);
  const clearHoverTimerRef = useRef(null);

  const cancelHoverClear = () => {
    if (clearHoverTimerRef.current) {
      clearTimeout(clearHoverTimerRef.current);
      clearHoverTimerRef.current = null;
    }
  };

  const scheduleHoverClear = () => {
    cancelHoverClear();
    clearHoverTimerRef.current = setTimeout(() => {
      if (!menuHoveredRef.current) standEditor.setHoveredStand(null);
      clearHoverTimerRef.current = null;
    }, 220);
  };

  const [gridWidth,  setGridWidth]  = useState(hall ? Math.ceil(hall.width_meters)  : 50);
  const [gridHeight, setGridHeight] = useState(hall ? Math.ceil(hall.length_meters) : 30);
  const [gridData,   setGridData]   = useState([]);

  const [zoom,                  setZoom]                  = useState(1);
  const [layoutLoaded,          setLayoutLoaded]          = useState(false);
  const [layoutExistsOnServer,  setLayoutExistsOnServer]  = useState(false);
  const [cursorPosition,        setCursorPosition]        = useState({ x: 0, y: 0, show: false });
  const [isFullscreen,          setIsFullscreen]          = useState(false);

  const [isPanning,  setIsPanning]  = useState(false);
  const [panStart,   setPanStart]   = useState({ x: 0, y: 0 });
  const [panOffset,  setPanOffset]  = useState({ x: 0, y: 0 });

  // Draw mode: 'grid' | 'triangle' | 'circle' | 'column' | 'boundary' | 'bezier' | 'editBoundary' | 'ceilingZoneRect' | 'ceilingZoneBrush'
  const [drawMode, setDrawMode] = useState('grid');

  // Escape key exits fullscreen
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFullscreen]);

  // ── Global mouseup: handles mouse released outside the canvas ────────
  // Without this, if the user drags off-canvas, handleMouseUp never fires
  // and the selection box stays visible indefinitely.
  const handleMouseUpRef = useRef(null);
  useEffect(() => { handleMouseUpRef.current = handleMouseUp; });
  useEffect(() => {
    const listener = () => { if (handleMouseUpRef.current) handleMouseUpRef.current(); };
    window.addEventListener('mouseup', listener);
    return () => window.removeEventListener('mouseup', listener);
  // eslint-disable-next-line
  }, []);

  // ── Ceiling height zones ──────────────────────────────────────────
  const [ceilingZones,    setCeilingZones]    = useState([]);
  const [czPendingCells,  setCzPendingCells]  = useState([]);  // cells being painted/selected
  const [czBrushActive,   setCzBrushActive]   = useState(false);
  const [czModal,         setCzModal]         = useState({ show: false, isNew: true, zone: null });
  const [czEditPanel,     setCzEditPanel]     = useState(null); // { zone, clientX, clientY, editCH, editMH }

  const saveCeilingZones = useCallback(async (zones) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/halls/${hall.id}`, { ceiling_zones: zones },
        { headers: { Authorization: `Bearer ${token}` } });
      setCeilingZones(zones);
    } catch { toast.error('Tavan bölgeleri kaydedilemedi'); }
  // eslint-disable-next-line
  }, [hall?.id]);

  // Find which zone a given cell belongs to
  const getCellZone = useCallback((x, y) =>
    ceilingZones.find(z => z.cells.some(c => c.x === x && c.y === y)) ?? null,
  [ceilingZones]);

  // For a rectangular stand: return single zone, 'MULTI', or null
  const getZoneForArea = useCallback((areaX, areaY, areaW, areaH) => {
    const zoneIds = new Set();
    for (let y = areaY; y < areaY + areaH; y++) {
      for (let x = areaX; x < areaX + areaW; x++) {
        const z = getCellZone(x, y);
        if (z) zoneIds.add(z.id);
      }
    }
    if (zoneIds.size === 0) return null;
    if (zoneIds.size > 1) return 'MULTI';
    return ceilingZones.find(z => z.id === [...zoneIds][0]);
  }, [getCellZone, ceilingZones]);

  // Polygon (triangle) — click-based vertices
  const [freeformPath,    setFreeformPath]    = useState([]); // confirmed vertices
  const [freeformPreview, setFreeformPreview] = useState(null); // live cursor for preview line

  // Hall boundary
  const [boundaryPath,        setBoundaryPath]        = useState(null);   // saved polygon
  const [boundaryPreviewPath, setBoundaryPreviewPath] = useState([]);     // in-progress
  const [boundaryPreview,     setBoundaryPreview]     = useState(null);   // cursor

  // Boundary edit mode
  const [workingBoundaryPath,  setWorkingBoundaryPath]  = useState(null);  // live copy while editing
  const [draggingVertexIdx,    setDraggingVertexIdx]    = useState(null);  // index of dragged vertex

  // Background floor plan image
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);
  const [backgroundOpacity,  setBackgroundOpacity]  = useState(0.35);
  const [bgUploading,        setBgUploading]         = useState(false);

  // Column config
  const [columnType, setColumnType] = useState('square_column');
  const [columnW,    setColumnW]    = useState('2');
  const [columnH,    setColumnH]    = useState('2');
  const [columnColor, setColumnColor] = useState('#94a3b8');
  const [showColumnModal, setShowColumnModal] = useState(false);

  const [moveMode,     setMoveMode]     = useState(false);
  const [movingStand,  setMovingStand]  = useState(null);

  const [showSidesDialog, setShowSidesDialog] = useState(false);
  const [sidesStand,      setSidesStand]      = useState(null);

  // Hall resize modal
  const [showResizeModal, setShowResizeModal] = useState(false);
  const [resizeW, setResizeW] = useState('');
  const [resizeH, setResizeH] = useState('');

  // Hall boundary confirmation
  const [boundaryConfirm, setBoundaryConfirm] = useState(false);
  const [pendingBoundaryPath, setPendingBoundaryPath] = useState(null);

  const openResizeModal = () => {
    setResizeW(String(gridWidth));
    setResizeH(String(gridHeight));
    setShowResizeModal(true);
  };

  const saveHallResize = async () => {
    const w = parseInt(resizeW);
    const h = parseInt(resizeH);
    if (!w || !h || w < 5 || h < 5 || w > 500 || h > 500) {
      toast.error('Geçerli bir boyut girin (5–500 m)');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/halls/${hall.id}`,
        { width_meters: w, length_meters: h },
        { headers: { Authorization: `Bearer ${token}` } });

      // Resize gridData synchronously before state flush
      const newData = [];
      for (let row = 0; row < h; row++) {
        const newRow = [];
        for (let col = 0; col < w; col++) {
          newRow.push(gridData[row]?.[col] ?? 'empty');
        }
        newData.push(newRow);
      }

      setGridWidth(w);
      setGridHeight(h);
      setGridData(newData);
      setShowResizeModal(false);
      toast.success(`Hall boyutu güncellendi: ${w}m × ${h}m`);
      setTimeout(() => autoFitZoom(), 150);
    } catch { toast.error('Boyut güncellenemedi'); }
  };
  const gridEditor = GridEditor({ gridWidth, gridHeight, gridData, setGridData, zoom });
  const standEditor = StandEditor({
    exhibition, hall, gridWidth, gridHeight, zoom,
    onStandsChange: () => {},
  });

  const { drawCanvas } = useCanvasDrawing({
    canvasRef, gridWidth, gridHeight, gridData, zoom,
    stands: standEditor.stands,
    selecting: standEditor.selecting,
    selectionStart: standEditor.selectionStart,
    selectionEnd: standEditor.selectionEnd,
    drawMode, freeformPath, freeformPreview,
    moveMode, movingStand, cursorPosition,
    getTextColor: standEditor.getTextColor,
    backgroundImageUrl, backgroundOpacity,
    boundaryPath: workingBoundaryPath || boundaryPath,   // live during edit
    boundaryPreviewPath,
    boundaryPreview,
    ceilingZones,
    czPendingCells,
  });

  const { loadLayout, saveLayout, initializeGrid } = useLayoutManager({
    exhibition, hall, gridWidth, gridHeight, gridData, setGridData,
    setGridWidth, setGridHeight, setLayoutLoaded, setLayoutExistsOnServer, layoutExistsOnServer,
  });

  // Escape: cancel move, polygon, or boundary edit
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (moveMode) { setMoveMode(false); setMovingStand(null); toast.info('Taşıma iptal edildi'); }
        if (freeformPath.length > 0) { setFreeformPath([]); setFreeformPreview(null); toast.info('Çizim iptal edildi'); }
        if (boundaryPreviewPath.length > 0) { setBoundaryPreviewPath([]); setBoundaryPreview(null); toast.info('Sınır çizimi iptal edildi'); }
        if (drawMode === 'editBoundary') { cancelBoundaryEdit(); toast.info('Sınır düzenleme iptal edildi'); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moveMode, freeformPath, drawMode]); // eslint-disable-line

  useEffect(() => {
    if (hall) {
      setGridWidth(Math.ceil(hall.width_meters));
      setGridHeight(Math.ceil(hall.length_meters));
      if (hall.boundary_path) {
        setBoundaryPath(hall.boundary_path);
        // Auto-fit to boundary after canvas renders
        setTimeout(() => fitToBoundary(hall.boundary_path), 300);
      }
      if (hall.background_image_url) setBackgroundImageUrl(hall.background_image_url);
      if (hall.ceiling_zones)        setCeilingZones(hall.ceiling_zones);
    }
    loadLayout();
  }, [exhibition?.id, hall?.id]); // eslint-disable-line

  useEffect(() => {
    if (gridData.length > 0) drawCanvas();
  }, [gridData, standEditor.stands, zoom, drawCanvas, gridWidth, gridHeight,
      freeformPath, freeformPreview,
      boundaryPath, workingBoundaryPath, boundaryPreviewPath, boundaryPreview, backgroundImageUrl, backgroundOpacity,
      ceilingZones, czPendingCells]);

  useEffect(() => {
    if (gridData.length > 0) autoFitZoom();
  }, [gridData.length]); // eslint-disable-line

  const autoFitZoom = () => {
    const container = containerRef.current;
    if (!container) return;
    setZoom(Math.min(
      (container.clientWidth - 40) / (gridWidth  * CELL_SIZE),
      600                           / (gridHeight * CELL_SIZE),
      2
    ));
  };

  // ── Fit canvas viewport to boundary bbox (1-cell margin) ──────────
  const fitToBoundary = (path) => {
    if (!path || path.length < 3) return;
    const container = containerRef.current;
    if (!container) return;

    const xs = path.map(p => p.x);
    const ys = path.map(p => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);

    const MARGIN = 1; // 1 hücre boşluk
    const bboxW = (maxX - minX) + MARGIN * 2;
    const bboxH = (maxY - minY) + MARGIN * 2;

    const containerW = Math.max(container.clientWidth  - 40, 400);
    const containerH = Math.max(container.clientHeight - 40, 400);

    const newZoom = Math.min(
      containerW / (bboxW  * CELL_SIZE),
      containerH / (bboxH  * CELL_SIZE),
      3
    );
    setZoom(newZoom);

    // Scroll to boundary (after zoom renders)
    setTimeout(() => {
      container.scrollLeft = Math.max(0, (minX - MARGIN) * CELL_SIZE * newZoom);
      container.scrollTop  = Math.max(0, (minY - MARGIN) * CELL_SIZE * newZoom);
    }, 60);
  };

  // ── Helpers ────────────────────────────────────────────────────────

  const getGridXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const s = CELL_SIZE * zoom;
    return {
      x: Math.floor((e.clientX - rect.left) / s),
      y: Math.floor((e.clientY - rect.top)  / s),
    };
  };

  // Snaps to nearest grid corner/intersection (for polygon/boundary/bezier drawing)
  const getCornerXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const s = CELL_SIZE * zoom;
    return {
      x: Math.round((e.clientX - rect.left) / s),
      y: Math.round((e.clientY - rect.top)  / s),
    };
  };

  // ── Point-in-polygon (ray casting) ────────────────────────────────
  const pointInPolygon = (px, py, polygon) => {
    if (!polygon || polygon.length < 3) return true; // no boundary = everywhere allowed
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      const intersect = ((yi > py) !== (yj > py)) &&
        (px < (xj - xi) * (py - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const isAreaInsideBoundary = (x, y, w, h) => {
    if (!boundaryPath || boundaryPath.length < 3) return true;
    // Check all 4 corners + center
    const pts = [
      { x, y }, { x: x + w - 1, y }, { x, y: y + h - 1 }, { x: x + w - 1, y: y + h - 1 },
      { x: x + Math.floor(w / 2), y: y + Math.floor(h / 2) },
    ];
    return pts.every(p => pointInPolygon(p.x + 0.5, p.y + 0.5, boundaryPath));
  };

  const saveBoundaryPath = (path) => {    // Show confirmation dialog instead of saving immediately
    setPendingBoundaryPath(path);
    setBoundaryConfirm(true);
  };

  // ── Catmull-Rom sampler → dense polygon for bezier boundary ──────────
  const catmullRomSample = (pts, samplesPerSeg = 18) => {
    const n = pts.length;
    if (n < 2) return pts;
    const out = [];
    for (let i = 0; i < n; i++) {
      const p0 = pts[(i - 1 + n) % n];
      const p1 = pts[i];
      const p2 = pts[(i + 1) % n];
      const p3 = pts[(i + 2) % n];
      for (let t = 0; t < samplesPerSeg; t++) {
        const u = t / samplesPerSeg;
        const u2 = u * u, u3 = u2 * u;
        out.push({
          x: Math.round(0.5 * ((2 * p1.x) + (-p0.x + p2.x) * u + (2*p0.x - 5*p1.x + 4*p2.x - p3.x) * u2 + (-p0.x + 3*p1.x - 3*p2.x + p3.x) * u3) * 10) / 10,
          y: Math.round(0.5 * ((2 * p1.y) + (-p0.y + p2.y) * u + (2*p0.y - 5*p1.y + 4*p2.y - p3.y) * u2 + (-p0.y + 3*p1.y - 3*p2.y + p3.y) * u3) * 10) / 10,
        });
      }
    }
    return out;
  };

  const saveBezierPath = (pts) => {
    if (pts.length < 3) return;
    const dense = catmullRomSample(pts, 18);
    setPendingBoundaryPath(dense);
    setBoundaryConfirm(true);
  };

  const confirmSaveBoundary = async () => {
    const path = pendingBoundaryPath;
    if (!hall || !path) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/halls/${hall.id}`, { boundary_path: path },
        { headers: { Authorization: `Bearer ${token}` } });
      setBoundaryPath(path);
      setBoundaryPreviewPath([]);
      setBoundaryPreview(null);
      setBoundaryConfirm(false);
      setPendingBoundaryPath(null);
      setDrawMode('grid');
      toast.success('Hall sınırı kaydedildi');
      // Auto-fit viewport to boundary
      setTimeout(() => fitToBoundary(path), 80);
    } catch { toast.error('Sınır kaydedilemedi'); }
  };

  const clearBoundary = async () => {
    if (!hall) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/halls/${hall.id}`, { boundary_path: null },
        { headers: { Authorization: `Bearer ${token}` } });
      setBoundaryPath(null);
      setWorkingBoundaryPath(null);
      setDraggingVertexIdx(null);
      setBoundaryPreviewPath([]);
      setDrawMode('grid');
      toast.success('Hall sınırı kaldırıldı');
      // Return to full-grid view
      setTimeout(() => autoFitZoom(), 80);
    } catch { toast.error('Sınır kaldırılamadı'); }
  };

  // ── Boundary edit mode ─────────────────────────────────────────────
  const cancelBoundaryEdit = () => {
    setWorkingBoundaryPath(null);
    setDraggingVertexIdx(null);
    setDrawMode('grid');
  };

  const saveBoundaryEdit = async () => {
    if (!workingBoundaryPath || !hall) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/halls/${hall.id}`, { boundary_path: workingBoundaryPath },
        { headers: { Authorization: `Bearer ${token}` } });
      setBoundaryPath(workingBoundaryPath);
      setWorkingBoundaryPath(null);
      setDraggingVertexIdx(null);
      setDrawMode('grid');
      toast.success('Sınır güncellendi');
      setTimeout(() => fitToBoundary(workingBoundaryPath), 80);
    } catch { toast.error('Sınır güncellenemedi'); }
  };

  const uploadBackground = async (file) => {
    if (!hall) { toast.error('Hall bulunamadı'); return; }
    setBgUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/upload/hall-background`, fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      const url = res.data.url;
      await axios.patch(`${API}/halls/${hall.id}`, { background_image_url: url },
        { headers: { Authorization: `Bearer ${token}` } });
      setBackgroundImageUrl(url);
      toast.success('Zemin planı yüklendi');
    } catch { toast.error('Yükleme başarısız'); } finally { setBgUploading(false); }
  };

  const removeBackground = async () => {
    if (!hall) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/halls/${hall.id}`, { background_image_url: null },
        { headers: { Authorization: `Bearer ${token}` } });
      setBackgroundImageUrl(null);
      toast.success('Arkaplan kaldırıldı');
    } catch { toast.error('Kaldırılamadı'); }
  };

  const closeFreeformAndCreate = (path) => {
    if (path.length < 3) return;
    const xs = path.map(p => p.x), ys = path.map(p => p.y);
    const x1 = Math.min(...xs), y1 = Math.min(...ys);
    const w  = Math.max(...xs) - x1 + 1, h = Math.max(...ys) - y1 + 1;
    if (w > 0 && h > 0) {
      if (!isAreaInsideBoundary(x1, y1, w, h)) {
        toast.error('Çizim hol sınırı dışına taşıyor!');
        setFreeformPath([]); setFreeformPreview(null); return;
      }
      standEditor.setSelectedArea({ x: x1, y: y1, width: w, height: h, path });
      standEditor.resetFormToDefaults();
      standEditor.setShowCreateDialog(true);
    }
    setFreeformPath([]);
    setFreeformPreview(null);
  };

  // ── Canvas events ──────────────────────────────────────────────────

  const handleCanvasClick = (e) => {
    if (e.detail === 2) return; // ignore – handled by dblclick
    const { x: gx, y: gy } = getGridXY(e);
    const { x: cx, y: cy } = getCornerXY(e);

    // ── Corner-snap modes (boundary / bezier / polygon) ─────────────────
    // Hall boundary drawing
    if (drawMode === 'boundary') {
      if (boundaryPreviewPath.length === 0) {
        setBoundaryPreviewPath([{ x: cx, y: cy }]); return;
      }
      const first = boundaryPreviewPath[0];
      if (Math.abs(cx - first.x) + Math.abs(cy - first.y) <= 1 && boundaryPreviewPath.length >= 3) {
        saveBoundaryPath(boundaryPreviewPath); return;
      }
      setBoundaryPreviewPath(prev => [...prev, { x: cx, y: cy }]);
      return;
    }

    // Bezier (smooth curve) boundary drawing
    if (drawMode === 'bezier') {
      if (boundaryPreviewPath.length === 0) {
        setBoundaryPreviewPath([{ x: cx, y: cy }]); return;
      }
      const first = boundaryPreviewPath[0];
      if (Math.abs(cx - first.x) + Math.abs(cy - first.y) <= 1 && boundaryPreviewPath.length >= 3) {
        saveBezierPath(boundaryPreviewPath); return;
      }
      setBoundaryPreviewPath(prev => [...prev, { x: cx, y: cy }]);
      return;
    }

    // Polygon / triangle / freeform
    if (drawMode === 'triangle' || drawMode === 'freeform') {
      if (freeformPath.length === 0) { setFreeformPath([{ x: cx, y: cy }]); return; }
      const first = freeformPath[0];
      if (Math.abs(cx - first.x) + Math.abs(cy - first.y) <= 1 && freeformPath.length >= 3) {
        closeFreeformAndCreate(freeformPath); return;
      }
      setFreeformPath(prev => [...prev, { x: cx, y: cy }]);
      return;
    }

    // ── Grid-snap modes ──────────────────────────────────────────────────
    const x = gx, y = gy;
    if (x < 0 || x >= gridWidth || y < 0 || y >= gridHeight) return;

    // Column placement is now handled by drag in handleMouseDown/Up
    // Grid mode: single-click corridor painting
    if (drawMode === 'grid') {
      gridEditor.updateCell(x, y);
    }
  };

  const handleDoubleClick = () => {
    if ((drawMode === 'triangle' || drawMode === 'freeform') && freeformPath.length >= 3) {
      closeFreeformAndCreate(freeformPath);
    }
    if (drawMode === 'boundary' && boundaryPreviewPath.length >= 3) {
      saveBoundaryPath(boundaryPreviewPath);
    }
    if (drawMode === 'bezier' && boundaryPreviewPath.length >= 3) {
      saveBezierPath(boundaryPreviewPath);
    }
  };

  const handleMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const s = CELL_SIZE * zoom;
    const x = Math.floor((e.clientX - rect.left) / s);
    const y = Math.floor((e.clientY - rect.top)  / s);

    if (isPanning) {
      const c = containerRef.current;
      if (c) { c.scrollLeft = panOffset.x - (e.clientX - panStart.x); c.scrollTop = panOffset.y - (e.clientY - panStart.y); }
      return;
    }

    // ── Ceiling zone brush painting ───────────────────────────────────
    if (drawMode === 'ceilingZoneBrush' && czBrushActive) {
      if (x >= 0 && x < gridWidth && y >= 0 && y < gridHeight) {
        setCzPendingCells(prev => {
          if (prev.some(c => c.x === x && c.y === y)) return prev;
          // Prevent painting over an already-assigned zone cell
          if (getCellZone(x, y) !== null) return prev;
          return [...prev, { x, y }];
        });
      }
      return;
    }

    // ── Ceiling zone rect: show live selection (reuse selecting state) ──
    if (drawMode === 'ceilingZoneRect' && standEditor.selecting) {
      standEditor.setSelectionEnd({ x, y });
      setCzPendingCells(() => {
        if (!standEditor.selectionStart) return [];
        const x1 = Math.min(standEditor.selectionStart.x, x);
        const y1 = Math.min(standEditor.selectionStart.y, y);
        const x2 = Math.max(standEditor.selectionStart.x, x);
        const y2 = Math.max(standEditor.selectionStart.y, y);
        const cells = [];
        for (let cy = y1; cy <= y2; cy++)
          for (let cx = x1; cx <= x2; cx++)
            // Prevent selecting cells that already belong to a zone
            if (getCellZone(cx, cy) === null) cells.push({ x: cx, y: cy });
        return cells;
      });
      drawCanvas();
      return;
    }

    // ── Boundary vertex drag ──────────────────────────────────────────
    if (drawMode === 'editBoundary' && draggingVertexIdx !== null && workingBoundaryPath) {
      const rawX = (e.clientX - rect.left) / s;
      const rawY = (e.clientY - rect.top)  / s;
      const newPath = [...workingBoundaryPath];
      newPath[draggingVertexIdx] = {
        x: Math.max(0, Math.min(gridWidth,  Math.round(rawX * 10) / 10)),
        y: Math.max(0, Math.min(gridHeight, Math.round(rawY * 10) / 10)),
      };
      setWorkingBoundaryPath(newPath);
      drawCanvas();
      return;
    }

    // Polygon live preview (corner-snap)
    const cx2 = Math.round((e.clientX - rect.left) / s);
    const cy2 = Math.round((e.clientY - rect.top)  / s);
    if ((drawMode === 'triangle' || drawMode === 'freeform') && freeformPath.length > 0) setFreeformPreview({ x: cx2, y: cy2 });
    if ((drawMode === 'boundary' || drawMode === 'bezier') && boundaryPreviewPath.length > 0) setBoundaryPreview({ x: cx2, y: cy2 });

    if (moveMode && movingStand) {
      setCursorPosition({ x, y, show: true, clientX: e.clientX, clientY: e.clientY });
      drawCanvas(); return;
    }
    if (standEditor.draggingStand) {
      const nx = Math.max(0, Math.min(gridWidth  - standEditor.draggingStand.width,  x - standEditor.dragOffset.x));
      const ny = Math.max(0, Math.min(gridHeight - standEditor.draggingStand.height, y - standEditor.dragOffset.y));
      standEditor.setDraggingStand({ ...standEditor.draggingStand, x: nx, y: ny });
      drawCanvas(); return;
    }
    if (standEditor.selecting) {
      standEditor.setSelectionEnd({ x, y }); drawCanvas(); return;
    }

    // Hover with debounce — mouse gap between stand and menu is handled gracefully
    const foundStand = standEditor.stands.find(
      s => x >= s.x && x < s.x + s.width && y >= s.y && y < s.y + s.height
    );
    if (foundStand) {
      cancelHoverClear();
      standEditor.setHoveredStand(foundStand);
    } else if (!menuHoveredRef.current) {
      scheduleHoverClear();
    }

    if (x >= 0 && x < gridWidth && y >= 0 && y < gridHeight)
      setCursorPosition({ x, y, show: true, clientX: e.clientX, clientY: e.clientY });
    else
      setCursorPosition({ x: 0, y: 0, show: false });
  };

  const handleMouseLeave = () => {
    gridEditor.setIsDrawing(false);
    setIsPanning(false);
    setCursorPosition({ x: 0, y: 0, show: false });
    scheduleHoverClear();
  };

  const handleMouseDown = (e) => {
    const { x, y } = getGridXY(e);

    // Pan
    if (e.button === 1 || (e.button === 0 && e.shiftKey)) {
      e.preventDefault(); setIsPanning(true); setPanStart({ x: e.clientX, y: e.clientY });
      const c = containerRef.current;
      if (c) setPanOffset({ x: c.scrollLeft, y: c.scrollTop });
      return;
    }

    // Place in move mode
    if (moveMode && movingStand && e.button === 0) {
      const nx = Math.max(0, Math.min(gridWidth  - movingStand.width,  x));
      const ny = Math.max(0, Math.min(gridHeight - movingStand.height, y));
      const ov = standEditor.stands.find(s => s.id !== movingStand.id &&
        !(nx + movingStand.width <= s.x || nx >= s.x + s.width || ny + movingStand.height <= s.y || ny >= s.y + s.height));
      if (ov) { toast.error(`Bu konum ${ov.stand_number} ile çakışıyor!`); return; }
      const moveZone = getZoneForArea(nx, ny, movingStand.width, movingStand.height);
      if (moveZone === 'MULTI') { toast.error('Stand birden fazla tavan yüksekliği bölgesine geçemez!'); return; }
      (async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.patch(`${API}/stands/${movingStand.id}`, { x: nx, y: ny },
            { headers: { Authorization: `Bearer ${token}` } });
          toast.success('Stand taşındı');
          setMoveMode(false); setMovingStand(null);
          const res = await axios.get(
            `${API}/stands?exhibition_id=${exhibition.id}&hall_id=${hall.id}`,
            { headers: { Authorization: `Bearer ${token}` } });
          standEditor.setStands(res.data);
        } catch { toast.error('Stand taşınamadı'); }
      })();
      return;
    }

    // Triangle, freeform, boundary, bezier handled purely by click events — no selection
    if (drawMode === 'triangle' || drawMode === 'freeform' || drawMode === 'boundary' || drawMode === 'bezier') return;

    // ── Column drag-to-place ──────────────────────────────────────────
    if (drawMode === 'column' && e.button === 0) {
      const parseCol = (v) => parseFloat(String(v).replace(',', '.')) || 2;
      const cW = parseCol(columnW);
      const cH = parseCol(columnH);
      const colX = Math.max(0, Math.min(gridWidth  - cW, x));
      const colY = Math.max(0, Math.min(gridHeight - cH, y));
      // Sync cursorPosition to click point so mouseup uses correct position for a simple click
      setCursorPosition({ x, y, show: true, clientX: e.clientX, clientY: e.clientY });
      setMovingStand({
        id: '__new_column__', x: colX, y: colY,
        width: cW, height: cH,
        is_column: true, shape_type: columnType,
        status: 'müsait', color: columnColor, opacity: 1,
        stand_number: '', isNewColumn: true,
      });
      setMoveMode(true);
      return;
    }

    // ── Ceiling zone modes ────────────────────────────────────────────
    if ((drawMode === 'ceilingZoneRect' || drawMode === 'ceilingZoneBrush') && e.button === 0) {
      // Click on existing zone cell → open edit panel
      const clickedZone = getCellZone(x, y);
      if (clickedZone) {
        setCzEditPanel({
          zone: clickedZone,
          clientX: e.clientX, clientY: e.clientY,
          editCH: String(clickedZone.ceiling_height),
          editMH: String(clickedZone.max_stand_height),
        });
        return;
      }
      if (drawMode === 'ceilingZoneBrush') {
        setCzBrushActive(true);
        if (x >= 0 && x < gridWidth && y >= 0 && y < gridHeight)
          setCzPendingCells([{ x, y }]);
        return;
      }
      // Rect mode: use selection mechanism
      standEditor.setSelecting(true);
      standEditor.setSelectionStart({ x, y });
      standEditor.setSelectionEnd({ x, y });
      return;
    }

    // Boundary edit mode — grab nearest vertex
    if (drawMode === 'editBoundary') {
      if (!workingBoundaryPath) return;
      const clickX = e.clientX - canvasRef.current.getBoundingClientRect().left;
      const clickY = e.clientY - canvasRef.current.getBoundingClientRect().top;
      const scs = CELL_SIZE * zoom;
      const GRAB_RADIUS = Math.max(16, scs * 0.6);
      const nearIdx = workingBoundaryPath.findIndex(pt => {
        const vx = pt.x * scs + scs / 2;
        const vy = pt.y * scs + scs / 2;
        return Math.sqrt((vx - clickX) ** 2 + (vy - clickY) ** 2) < GRAB_RADIUS;
      });
      if (nearIdx !== -1) {
        setDraggingVertexIdx(nearIdx);
        e.preventDefault();
      }
      return;
    }

    // Check existing stand (skip selection start)
    const clicked = standEditor.stands.find(s =>
      x >= s.x && x < s.x + s.width && y >= s.y && y < s.y + s.height);
    if (clicked) return;

    if (e.button === 0) {
      standEditor.setSelecting(true);
      standEditor.setSelectionStart({ x, y });
      standEditor.setSelectionEnd({ x, y });
    }
  };

  const handleMouseUp = () => {
    // Release boundary vertex drag
    if (drawMode === 'editBoundary' && draggingVertexIdx !== null) {
      setDraggingVertexIdx(null);
      return;
    }

    // ── Ceiling zone brush — end stroke → open modal ──────────────────
    if (drawMode === 'ceilingZoneBrush' && czBrushActive) {
      setCzBrushActive(false);
      if (czPendingCells.length > 0) {
        setCzModal({ show: true, isNew: true, zone: null });
      }
      return;
    }

    // ── Ceiling zone rect — convert selection to cells → open modal ────
    if (drawMode === 'ceilingZoneRect' && standEditor.selecting && standEditor.selectionStart && standEditor.selectionEnd) {
      const x1 = Math.min(standEditor.selectionStart.x, standEditor.selectionEnd.x);
      const y1 = Math.min(standEditor.selectionStart.y, standEditor.selectionEnd.y);
      const x2 = Math.max(standEditor.selectionStart.x, standEditor.selectionEnd.x);
      const y2 = Math.max(standEditor.selectionStart.y, standEditor.selectionEnd.y);
      standEditor.setSelecting(false);
      standEditor.setSelectionStart(null);
      standEditor.setSelectionEnd(null);
      const cells = [];
      for (let cy = y1; cy <= y2; cy++)
        for (let cx = x1; cx <= x2; cx++)
          // Skip cells already assigned to a zone
          if (getCellZone(cx, cy) === null) cells.push({ x: cx, y: cy });
      if (cells.length > 0) {
        setCzPendingCells(cells);
        setCzModal({ show: true, isNew: true, zone: null });
      }
      return;
    }

    if (standEditor.selecting && standEditor.selectionStart && standEditor.selectionEnd) {
      const x1 = Math.min(standEditor.selectionStart.x, standEditor.selectionEnd.x);
      const y1 = Math.min(standEditor.selectionStart.y, standEditor.selectionEnd.y);
      const x2 = Math.max(standEditor.selectionStart.x, standEditor.selectionEnd.x);
      const y2 = Math.max(standEditor.selectionStart.y, standEditor.selectionEnd.y);
      const w  = x2 - x1 + 1, h = y2 - y1 + 1;

      if (w > 0 && h > 0) {
        // Grid mode: 1x1 selection = single click → corridor painting handled by handleCanvasClick, skip dialog
        if (drawMode === 'grid' && w === 1 && h === 1) {
          standEditor.setSelecting(false);
          standEditor.setSelectionStart(null);
          standEditor.setSelectionEnd(null);
          return;
        }
        // Check boundary constraint
        if (!isAreaInsideBoundary(x1, y1, w, h)) {
          toast.error('Stand hol sınırı dışına çizilemez!');
          standEditor.setSelecting(false);
          standEditor.setSelectionStart(null);
          standEditor.setSelectionEnd(null);
          return;
        }
        // ── Ceiling zone constraint (stand must not span multiple zones) ──
        const zone = getZoneForArea(x1, y1, w, h);
        if (zone === 'MULTI') {
          toast.error('Stand birden fazla tavan yüksekliği bölgesine geçemez!');
          standEditor.setSelecting(false);
          standEditor.setSelectionStart(null);
          standEditor.setSelectionEnd(null);
          return;
        }
        const extra = drawMode === 'circle' ? { shape_type: 'circle' } : {};
        standEditor.setSelectedArea({
          x: x1, y: y1, width: w, height: h,
          ceiling_height: zone?.ceiling_height ?? null,
          max_stand_height: zone?.max_stand_height ?? null,
          ...extra,
        });
        standEditor.resetFormToDefaults();
        // Pre-fill width, height and ceiling heights (zone → exhibition default → empty)
        standEditor.setStandForm(prev => ({
          ...prev,
          width: w,
          height: h,
          ...(zone ? {
            ceiling_height: String(zone.ceiling_height),
            max_stand_height: String(zone.max_stand_height),
          } : exhibition?.default_ceiling_height ? {
            ceiling_height: String(exhibition.default_ceiling_height),
            max_stand_height: String(exhibition.default_max_stand_height || ''),
          } : {}),
        }));
        standEditor.setShowCreateDialog(true);
      }
      standEditor.setSelecting(false);
      standEditor.setSelectionStart(null);
      standEditor.setSelectionEnd(null);
    }
    if (standEditor.draggingStand) {
      const ds = standEditor.draggingStand;
      const zone = getZoneForArea(ds.x, ds.y, ds.width, ds.height);
      if (zone === 'MULTI') {
        toast.error('Stand birden fazla tavan yüksekliği bölgesine geçemez!');
        standEditor.setDraggingStand(null); // reverts to original position in stands[]
      } else {
        standEditor.updateStandPosition(ds);
        standEditor.setDraggingStand(null);
      }
    }

    // ── New column placed by drag ─────────────────────────────────────
    if (moveMode && movingStand?.isNewColumn) {
      const ds = movingStand;
      setMoveMode(false);
      setMovingStand(null);
      // Circle columns: center the circle on the cursor position
      const isCircle = ds.shape_type === 'circle_column';
      const finalX = Math.max(0, Math.min(gridWidth  - ds.width,
        isCircle ? cursorPosition.x - ds.width  / 2 : cursorPosition.x));
      const finalY = Math.max(0, Math.min(gridHeight - ds.height,
        isCircle ? cursorPosition.y - ds.height / 2 : cursorPosition.y));
      if (!isAreaInsideBoundary(finalX, finalY, ds.width, ds.height)) {
        toast.error('Kolon hol sınırı dışına yerleştirilemez!'); return;
      }
      const ov = standEditor.stands.find(s =>
        !(finalX + ds.width <= s.x || finalX >= s.x + s.width || finalY + ds.height <= s.y || finalY >= s.y + s.height));
      if (ov) { toast.error('Başka bir nesneyle çakışıyor!'); return; }
      (async () => {
        try {
          const token = localStorage.getItem('token');
          const colNum = `K-${String(standEditor.stands.filter(s => s.is_column).length + 1).padStart(2, '0')}`;
          const body = {
            exhibition_id: exhibition.id, hall_id: hall.id,
            stand_number: colNum, stand_name: '',
            x: finalX, y: finalY,
            width: parseFloat(String(ds.width).replace(',', '.')),
            height: parseFloat(String(ds.height).replace(',', '.')),
            is_column: true, shape_type: ds.shape_type,
            status: 'müsait', color: ds.color, opacity: 1,
          };
          const res = await axios.post(`${API}/stands`, body, { headers: { Authorization: `Bearer ${token}` } });
          standEditor.setStands(prev => [...prev, res.data]);
          toast.success(`${colNum} eklendi`);
        } catch { toast.error('Kolon eklenemedi!'); }
      })();
      return;
    }
    setIsPanning(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    setZoom(prev => Math.max(0.3, Math.min(3, prev + (e.deltaY > 0 ? -0.1 : 0.1))));
  };

  const handleCopyStand = async (stand) => {
    standEditor.setHoveredStand(null);
    const newStand = await standEditor.copyStand(stand);
    if (newStand) { setMoveMode(true); setMovingStand(newStand); toast.info('Stand kopyalandı. Yeni konuma tıklayın veya Esc ile iptal.'); }
  };

  const handleExportPNG = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    link.download = `${(exhibition?.name || 'fuar').replace(/\s+/g,'_')}_${(hall?.name || 'hol').replace(/\s+/g,'_')}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
    toast.success('PNG indirildi');
  };

  return (
    <Card className={isFullscreen
      ? "fixed inset-0 z-[100] flex flex-col rounded-none border-0 shadow-none mt-0"
      : "mt-4"
    }>
      <CardHeader className={isFullscreen ? "flex-shrink-0 border-b px-4 py-3" : ""}>
        <CardTitle className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate">
              {hall
                ? `${hall.name} - Kroki Editörü (${gridWidth}m × ${gridHeight}m = ${(gridWidth * gridHeight).toFixed(0)}m²)`
                : `Kroki Editörü - ${exhibition.name}`}
            </span>
            {hall && (
              <button
                onClick={openResizeModal}
                title="Hall boyutunu değiştir"
                data-testid="open-resize-modal-btn"
                className="flex-shrink-0 p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setIsFullscreen(f => !f)}
            title={isFullscreen ? "Küçült" : "Tam Ekran"}
            data-testid="toggle-fullscreen-btn"
            className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </CardTitle>
      </CardHeader>

      {/* ── Hall Resize Modal ── */}
      {showResizeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Hall Boyutunu Değiştir</h2>
              <button onClick={() => setShowResizeModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="resize-w" className="text-sm font-medium">Genişlik (m)</Label>
                <Input
                  id="resize-w"
                  type="number"
                  min="5" max="500"
                  value={resizeW}
                  onChange={e => setResizeW(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saveHallResize()}
                  className="text-center text-lg font-bold"
                  autoFocus
                  data-testid="resize-width-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="resize-h" className="text-sm font-medium">Boy (m)</Label>
                <Input
                  id="resize-h"
                  type="number"
                  min="5" max="500"
                  value={resizeH}
                  onChange={e => setResizeH(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saveHallResize()}
                  className="text-center text-lg font-bold"
                  data-testid="resize-height-input"
                />
              </div>
            </div>

            {resizeW && resizeH && (
              <p className="text-sm text-gray-500 text-center">
                Toplam alan: <strong>{(parseInt(resizeW) || 0) * (parseInt(resizeH) || 0)} m²</strong>
              </p>
            )}

            <div className="flex gap-3 pt-1">
              <Button
                onClick={saveHallResize}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="confirm-resize-btn">
                Kaydet
              </Button>
              <Button variant="outline" onClick={() => setShowResizeModal(false)} className="flex-1">
                İptal
              </Button>
            </div>
          </div>
        </div>
      )}
      <CardContent className={isFullscreen ? "flex-1 overflow-hidden flex flex-col p-4 min-h-0" : ""}>
        <div className={isFullscreen ? "flex flex-col h-full gap-4" : "space-y-4"}>
          {!layoutLoaded && !hall && (
            <div className="flex gap-4 items-end">
              <div className="space-y-2">
                <Label>Genişlik (m)</Label>
                <Input type="number" value={gridWidth}
                  onChange={e => setGridWidth(parseInt(e.target.value) || 50)}
                  min="10" max="100" data-testid="grid-width-input" />
              </div>
              <div className="space-y-2">
                <Label>Yükseklik (m)</Label>
                <Input type="number" value={gridHeight}
                  onChange={e => setGridHeight(parseInt(e.target.value) || 30)}
                  min="10" max="100" data-testid="grid-height-input" />
              </div>
              <Button onClick={() => initializeGrid(gridWidth, gridHeight)} data-testid="create-grid-btn">
                Grid Oluştur
              </Button>
            </div>
          )}

          {gridData.length > 0 && (
            <>
              <div className={isFullscreen ? "flex-shrink-0" : ""}>
              <CanvasToolbar
                drawMode={drawMode}
                setDrawMode={(m) => {
                  // Entering editBoundary: use existing boundary OR create default rectangle
                  if (m === 'editBoundary') {
                    const path = boundaryPath
                      ? [...boundaryPath]
                      : [
                          { x: 0, y: 0 },
                          { x: gridWidth, y: 0 },
                          { x: gridWidth, y: gridHeight },
                          { x: 0, y: gridHeight },
                        ];
                    setWorkingBoundaryPath(path);
                  }
                  // Leaving editBoundary without saving: discard
                  if (drawMode === 'editBoundary' && m !== 'editBoundary') {
                    setWorkingBoundaryPath(null);
                    setDraggingVertexIdx(null);
                  }
                  setDrawMode(m);
                  setFreeformPath([]);
                  setFreeformPreview(null);
                  setBoundaryPreviewPath([]);
                  setBoundaryPreview(null);
                }}
                gridEditor={gridEditor}
                zoom={zoom} setZoom={setZoom}
                onSave={saveLayout}
                onExportPNG={handleExportPNG}
                onOpenColumnConfig={() => setShowColumnModal(true)}
                backgroundImageUrl={backgroundImageUrl}
                backgroundOpacity={backgroundOpacity}
                setBackgroundOpacity={setBackgroundOpacity}
                onUploadBackground={uploadBackground}
                onRemoveBackground={removeBackground}
                bgUploading={bgUploading}
                boundaryPath={boundaryPath}
                onClearBoundary={clearBoundary}
                onFitToBoundary={() => fitToBoundary(boundaryPath)}
                onSaveBoundaryEdit={saveBoundaryEdit}
                onCancelBoundaryEdit={cancelBoundaryEdit}
                boundaryPreviewPath={boundaryPreviewPath}
                ceilingZones={ceilingZones}
              />
              </div>

              <div
                ref={containerRef}
                className="overflow-auto border rounded-lg bg-gray-100 p-4 relative"
                style={{ maxHeight: isFullscreen ? undefined : '700px', flex: isFullscreen ? '1 1 0' : undefined, minHeight: isFullscreen ? 0 : undefined }}
                onWheel={handleWheel}
              >
                <canvas
                  ref={canvasRef}
                  onClick={handleCanvasClick}
                  onDoubleClick={handleDoubleClick}
                  onMouseDown={handleMouseDown}
                  onMouseLeave={handleMouseLeave}
                  onMouseMove={handleMouseMove}
                  className={isPanning ? 'cursor-grabbing' : moveMode && movingStand?.isNewColumn ? '' : moveMode ? 'cursor-move' : drawMode === 'editBoundary' ? (draggingVertexIdx !== null ? 'cursor-grabbing' : 'cursor-grab') : drawMode === 'column' ? '' : 'cursor-crosshair'}
                  style={(() => {
                    if (drawMode === 'column' && !isPanning) {
                      const cW = parseFloat(String(columnW).replace(',','.')) || 2;
                      const cH = parseFloat(String(columnH).replace(',','.')) || 2;
                      const ratio = cH / cW;
                      const w = 28;
                      const h = Math.max(12, Math.round(w * Math.min(ratio, 2)));
                      const r = parseInt(columnColor.slice(1,3),16);
                      const g = parseInt(columnColor.slice(3,5),16);
                      const b = parseInt(columnColor.slice(5,7),16);
                      const fill = `rgba(${r},${g},${b},0.45)`;
                      const stroke = columnColor;
                      let shape;
                      if (columnType === 'circle_column') {
                        const rx = w/2-1, ry = h/2-1;
                        shape = `<ellipse cx="${w/2}" cy="${h/2}" rx="${rx}" ry="${ry}" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`;
                      } else {
                        shape = `<rect x="1" y="1" width="${w-2}" height="${h-2}" rx="1" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`;
                      }
                      const svg = encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">${shape}</svg>`);
                      const hotX = columnType === 'circle_column' ? Math.round(w / 2) : 0;
                      const hotY = columnType === 'circle_column' ? Math.round(h / 2) : 0;
                      return { cursor: `url("data:image/svg+xml,${svg}") ${hotX} ${hotY}, crosshair` };
                    }
                    if (moveMode && movingStand?.isNewColumn) return { cursor: 'crosshair' };
                    return undefined;
                  })()}
                  data-testid="layout-canvas"
                />

                {/* Cursor tooltip */}
                {cursorPosition.show && !isPanning && !standEditor.selecting && (
                  <div
                    className="absolute bg-black/80 text-white px-2 py-1 rounded text-xs pointer-events-none z-50"
                    style={{
                      left: `${cursorPosition.clientX - (containerRef.current?.getBoundingClientRect().left || 0) + 12}px`,
                      top:  `${cursorPosition.clientY - (containerRef.current?.getBoundingClientRect().top  || 0) - 32}px`,
                    }}
                  >
                    {cursorPosition.x}m, {cursorPosition.y}m
                    {(drawMode === 'triangle' || drawMode === 'freeform') && freeformPath.length > 0 && ` · ${freeformPath.length} nokta`}
                  </div>
                )}

                {/* Hover menu */}
                {standEditor.hoveredStand && !standEditor.selecting && !moveMode &&
                  !standEditor.showEditDialog && !standEditor.showCreateDialog && !showSidesDialog && (
                  <StandHoverMenu
                    hoveredStand={standEditor.hoveredStand}
                    zoom={zoom}
                    onMove={(stand) => { setMoveMode(true); setMovingStand(stand); standEditor.setHoveredStand(null); toast.info('Yeni konuma tıklayın'); }}
                    onEdit={(stand) => standEditor.openEditDialog(stand)}
                    onCopy={handleCopyStand}
                    onDelete={(id) => standEditor.deleteStand(id)}
                    onSides={(stand) => { setSidesStand(stand); setShowSidesDialog(true); standEditor.setHoveredStand(null); }}
                    onContract={(stand) => { standEditor.openContractModal(stand); standEditor.setHoveredStand(null); }}
                    onMail={(stand) => { standEditor.openMailModal(stand); standEditor.setHoveredStand(null); }}
                    onMouseEnter={() => { menuHoveredRef.current = true; cancelHoverClear(); }}
                    onMouseLeave={() => { menuHoveredRef.current = false; scheduleHoverClear(); }}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </CardContent>

      {standEditor.CreateStandDialog()}
      {standEditor.EditStandDialog()}
      {standEditor.ContractModal()}
      <StandSidesDialog
        open={showSidesDialog} onOpenChange={setShowSidesDialog}
        stand={sidesStand} onUpdate={() => standEditor.fetchStands()}
      />

      {/* ── Ceiling Zone Modal (new zone) ───────────────────────────── */}
      <CeilingZoneModal
        show={czModal.show}
        initial={czModal.isNew ? { color_index: ceilingZones.length % 8 } : czModal.zone}
        pendingCells={czPendingCells.length}
        existingZones={ceilingZones}
        onClose={() => { setCzModal({ show: false, isNew: true, zone: null }); setCzPendingCells([]); }}
        onSave={(ch, mh, colorIdx) => {
          if (czModal.isNew) {
            const newZone = {
              id: `cz_${Date.now()}`,
              ceiling_height: ch,
              max_stand_height: mh,
              color_index: colorIdx,
              cells: czPendingCells,
            };
            const updated = [...ceilingZones, newZone];
            saveCeilingZones(updated);
          }
          setCzModal({ show: false, isNew: true, zone: null });
          setCzPendingCells([]);
        }}
      />

      {/* ── Ceiling Zone Edit Panel (click on existing zone) ─────────── */}
      {czEditPanel && (
        <div
          className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 w-72"
          style={{ top: Math.min(czEditPanel.clientY, window.innerHeight - 260), left: Math.min(czEditPanel.clientX + 12, window.innerWidth - 300) }}
          data-testid="cz-edit-panel"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700">Tavan Bölgesi Düzenle</span>
            <button onClick={() => setCzEditPanel(null)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Tavan Yüksekliği (m)</label>
              <input
                type="number" step="0.1" min="1"
                value={czEditPanel.editCH}
                onChange={e => setCzEditPanel(p => ({ ...p, editCH: e.target.value }))}
                className="w-full border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="cz-edit-ceiling"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Maks. Stand Yüksekliği (m)</label>
              <input
                type="number" step="0.1" min="0.5"
                value={czEditPanel.editMH}
                onChange={e => setCzEditPanel(p => ({ ...p, editMH: e.target.value }))}
                className="w-full border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="cz-edit-maxstand"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              size="sm" variant="destructive" className="gap-1 flex-1"
              data-testid="cz-edit-delete"
              onClick={() => {
                const updated = ceilingZones.filter(z => z.id !== czEditPanel.zone.id);
                saveCeilingZones(updated);
                setCzEditPanel(null);
                toast.success('Bölge silindi');
              }}
            >
              <Trash2 className="w-3.5 h-3.5" /> Sil
            </Button>
            <Button
              size="sm" className="gap-1 flex-1 bg-amber-500 hover:bg-amber-600 border-amber-500"
              data-testid="cz-edit-save"
              onClick={() => {
                const ch = parseFloat(czEditPanel.editCH);
                const mh = parseFloat(czEditPanel.editMH);
                if (!ch || ch <= 0 || !mh || mh <= 0 || mh > ch) {
                  toast.error('Geçersiz değerler'); return;
                }
                const updated = ceilingZones.map(z =>
                  z.id === czEditPanel.zone.id ? { ...z, ceiling_height: ch, max_stand_height: mh } : z
                );
                saveCeilingZones(updated);
                setCzEditPanel(null);
                toast.success('Bölge güncellendi');
              }}
            >
              Güncelle
            </Button>
          </div>
        </div>
      )}

      {/* ── Column Config Modal ─────────────────────────────────────── */}
      {showColumnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Kolon Yapılandır</h2>
              <button onClick={() => setShowColumnModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Shape selector */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Şekil</Label>
              <div className="flex gap-2">
                {COLUMN_TYPES.map(ct => (
                  <Button key={ct.type} size="sm"
                    variant={columnType === ct.type ? 'default' : 'outline'}
                    onClick={() => {
                      setColumnType(ct.type);
                      if (ct.type !== 'rect_column') setColumnH(columnW);
                    }}
                    className="flex-1 text-xs"
                    data-testid={`column-modal-type-${ct.type}`}>
                    {ct.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Dimensions */}
            {columnType === 'circle_column' ? (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700">Çap (m)</Label>
                <Input
                  type="text" inputMode="decimal"
                  value={columnW}
                  onChange={e => { const v = e.target.value; setColumnW(v); setColumnH(v); }}
                  className="text-center font-semibold"
                  data-testid="column-modal-width"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700">En (m)</Label>
                  <Input
                    type="text" inputMode="decimal"
                    value={columnW}
                    onChange={e => {
                      const v = e.target.value;
                      setColumnW(v);
                      if (columnType !== 'rect_column') setColumnH(v);
                    }}
                    className="text-center font-semibold"
                    data-testid="column-modal-width"
                  />
                </div>
                <div className={`space-y-1.5 transition-opacity ${columnType !== 'rect_column' ? 'opacity-40 pointer-events-none' : ''}`}>
                  <Label className="text-sm font-medium text-gray-700">Boy (m)</Label>
                  <Input
                    type="text" inputMode="decimal"
                    value={columnH}
                    onChange={e => setColumnH(e.target.value)}
                    className="text-center font-semibold"
                    data-testid="column-modal-height"
                  />
                </div>
              </div>
            )}

            {/* Color */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Renk</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={columnColor}
                  onChange={e => setColumnColor(e.target.value)}
                  className="w-10 h-10 rounded-lg cursor-pointer border border-gray-200"
                  data-testid="column-modal-color"
                />
                <div className="flex gap-1.5 flex-wrap">
                  {['#94a3b8','#1e293b','#dc2626','#2563eb','#16a34a','#d97706','#7c3aed'].map(c => (
                    <button
                      key={c}
                      onClick={() => setColumnColor(c)}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${columnColor === c ? 'border-gray-800 scale-110' : 'border-transparent hover:border-gray-400'}`}
                      style={{ backgroundColor: c }}
                      title={c}
                      data-testid={`column-color-preset-${c.slice(1)}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
              <span className="text-xs text-gray-500">Önizleme:</span>
              <div
                className={`border-2 ${columnType === 'circle_column' ? 'rounded-full' : 'rounded-sm'}`}
                style={{
                  width: columnType === 'rect_column' ? 40 : 28,
                  height: columnType === 'rect_column' ? 24 : 28,
                  backgroundColor: columnColor + '73',
                  borderColor: columnColor,
                }}
              />
              <span className="text-xs text-gray-600 font-medium">
                {columnType === 'circle_column'
                  ? `Çap: ${parseFloat(String(columnW).replace(',','.')) || 2}m`
                  : columnType === 'rect_column'
                    ? `${parseFloat(String(columnW).replace(',','.')) || 2}m × ${parseFloat(String(columnH).replace(',','.')) || 2}m`
                    : `${parseFloat(String(columnW).replace(',','.')) || 2}m`
                }
              </span>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <Button
                onClick={() => {
                  setDrawMode('column');
                  setShowColumnModal(false);
                }}
                className="flex-1"
                data-testid="column-modal-confirm">
                Yerleştir
              </Button>
              <Button variant="outline" onClick={() => setShowColumnModal(false)} className="flex-1"
                data-testid="column-modal-cancel">
                İptal
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hall Boundary Confirmation Dialog */}
      {boundaryConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-base font-bold text-gray-900 mb-1">Hall Sınırını Kaydet</h3>
            <p className="text-sm text-gray-600 mb-1">
              {pendingBoundaryPath?.length} noktalı polygon çizildi.
            </p>
            <p className="text-sm text-gray-500 mb-5">
              Bu alan holün fiziksel sınırı olarak kaydedilsin mi?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setBoundaryConfirm(false); setPendingBoundaryPath(null); setBoundaryPreviewPath([]); setBoundaryPreview(null); }}
                className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={confirmSaveBoundary}
                className="flex-1 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
              >
                Evet, Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
